import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteOwnerState,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderGroupParams,
  Box,
  CircularProgress,
  FilterOptionsState,
  ListItemText,
  SxProps,
  TextField,
  TextFieldVariants,
  Theme,
  Typography
} from '@mui/material';
import React, { ReactNode } from 'react';

interface AutocompleteFieldProps {
  sx?: SxProps<Theme>;
  topLabel?: string;
  disableTrimStart?: boolean;
  placeholder?: string;
  size?: 'medium' | 'small';
  backgroundColor?: string;
  renderOption?: any;
  error?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  variant?: TextFieldVariants;
  label?: string;
  shrinkLabel?: boolean;
  readOnly?: boolean;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  clearIcon?: ReactNode;
  options: any[];
  helperText?: string;
  freesolo?: boolean;
  heightDropdown?: string;
  multiple?: boolean;
  value?: any;
  defaultValue?: any;
  inputValue?: string;
  getOptionLabel?: (option: any) => string;
  filterOptions?: (options: any[], state: FilterOptionsState<any>) => any[];
  filterSelectedOptions?: boolean;
  onChange?: (
    event: React.SyntheticEvent,
    value: any,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any> | undefined
  ) => void;
  onInputChange?: (
    event: React.SyntheticEvent,
    value: string,
    reason?: AutocompleteInputChangeReason
  ) => void;
  onOpenDropdown?: (event: React.SyntheticEvent) => void;
  renderTags?: (
    value: any[],
    getTagProps: AutocompleteRenderGetTagProps,
    ownerState: AutocompleteOwnerState<any, boolean, false, boolean, 'div'>
  ) => React.ReactNode;
  groupBy?: (option: any) => string;
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
  getOptionDisabled?: (option: any) => boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
}

function AutocompleteField(props: AutocompleteFieldProps) {
  const {
    disableTrimStart,
    options,
    placeholder,
    value,
    defaultValue,
    inputValue,
    size,
    onChange,
    onInputChange,
    getOptionLabel,
    renderOption,
    error,
    helperText,
    freesolo,
    disabled,
    isLoading,
    filterOptions,
    filterSelectedOptions,
    variant,
    label,
    sx,
    shrinkLabel,
    heightDropdown,
    onOpenDropdown,
    multiple,
    renderTags,
    readOnly = false,
    groupBy,
    renderGroup,
    getOptionDisabled,
    isOptionEqualToValue,
    clearIcon,
    topLabel,
    onKeyDown
  } = props;

  const defaultRenderOption = (props: any, option: any) => {
    return (
      <>
        <ListItemText
          {...(props as any)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            // "alignItems": "center",
            overflowWrap: 'anywhere',
            textAlign: 'left',
            color: option.disabled ? 'grey' : 'inherit',
            opacity: option.disabled ? 0.5 : 1,
            fontWeight: option.disabled ? '400' : '600'
          }}
          id={option.value}
          primary={option.name + ' ' + (option.disabledPostfix ?? '')}
          onClick={option.disabledPostfix ? (e: any) => e.stopPropagation() : props.onClick}
        />
      </>
    );
  };
  return (
    <Box sx={{ ...sx }}>
      {topLabel && (
        <Typography variant="subtitle2" sx={{ mb: '.5rem', fontWeight: '600' }}>
          {topLabel}
        </Typography>
      )}
      <Autocomplete
        clearIcon={clearIcon}
        onOpen={onOpenDropdown}
        disabled={disabled}
        size={size}
        defaultValue={defaultValue}
        multiple={multiple}
        renderTags={renderTags}
        freeSolo={freesolo}
        groupBy={groupBy}
        renderGroup={renderGroup}
        getOptionDisabled={getOptionDisabled}
        // disablePortal
        options={options}
        noOptionsText={
          <Typography variant="subtitle2" sx={{ fontSize: size === 'small' ? '1.2rem' : '1.3rem' }}>
            No options
          </Typography>
        }
        ListboxProps={{
          sx: {
            fontSize: size === 'small' ? '1.2rem' : '1.3rem',
            maxHeight: heightDropdown ? heightDropdown : 'auto'
          }
        }}
        value={value}
        onChange={onChange}
        inputValue={inputValue}
        onInputChange={(e, newValue) => {
          if (!onInputChange) return;
          onInputChange(e, disableTrimStart ? newValue : newValue.trimStart());
        }}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        filterSelectedOptions={filterSelectedOptions}
        renderOption={renderOption || defaultRenderOption}
        isOptionEqualToValue={(option, value) => {
          if (isOptionEqualToValue) return isOptionEqualToValue(option, value);
          if (value === undefined || value === '' || value === null) return true;
          return option === value;
        }}
        sx={sx}
        renderInput={(params) => (
          <TextField
            {...params}
            onKeyDown={(e) => {
              if (onKeyDown) {
                onKeyDown(e);
              }
            }}
            variant={variant ?? 'filled'}
            size={size ?? 'medium'}
            inputProps={{
              ...params.inputProps,
              readOnly: readOnly,
              style: {
                fontFamily: 'Source Sans 3',
                fontSize: '1.4rem'
              }
            }}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText?.toLowerCase() !== 'required' ? helperText : null}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress size={'1.25rem'} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              )
            }}
            InputLabelProps={{
              shrink: shrinkLabel,
              style: {
                fontFamily: 'Source Sans 3'
                // fontSize: '1.32rem'
                // fontSize: "1.32rem",
              }
            }}
          />
        )}
      />
    </Box>
  );
}

export default AutocompleteField;
